import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { openPrivacy, whatsappUrl } from "@/lib/site";

const TIPI = ["B&B", "Casa vacanze", "Villa", "Affittacamere", "Appartamenti / residence", "Altro"];

const inputCls =
  "w-full rounded-2xl border border-[#383029] bg-transparent px-5 py-3.5 text-sm text-[#FAF7F2] placeholder-[#A89F91] outline-none transition-colors duration-300 focus:border-[#E05A2C]";

const EMPTY = { name: "", phone: "", email: "", city: "", propertyType: "", sqm: "", listingUrl: "", message: "", consent: false };

export default function SopralluogoModal() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("trasformare");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handler = (e) => {
      setMode(e.detail === "rinnovare" ? "rinnovare" : "trasformare");
      setDone(false);
      setError("");
      setOpen(true);
    };
    window.addEventListener("open-sopralluogo", handler);
    return () => window.removeEventListener("open-sopralluogo", handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const set = (k) => (e) => {
    setError("");
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  };
  const rinnovo = mode === "rinnovare";

  const submit = async () => {
    if (form.name.trim().length < 2) return setError("Dimmi come ti chiami.");
    if (!/^\+?[0-9\s.-]{8,18}$/.test(form.phone.trim())) return setError("Inserisci un numero di telefono valido.");
    if (form.city.trim().length < 2) return setError("In che città si trova l'immobile?");
    if (!form.consent) return setError("Serve il consenso per essere ricontattato.");
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-visit-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          city: form.city.trim(),
          mode,
          property_type: form.propertyType,
          sqm: form.sqm.trim(),
          listing_url: form.listingUrl.trim(),
          message: form.message.trim(),
          consent: form.consent,
        }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      setForm(EMPTY);
    } catch {
      setError("Qualcosa non ha funzionato. Riprova tra poco, o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  const waText = rinnovo
    ? `Ciao, ho già una struttura a ${form.city || "[città]"} e vorrei far valutare gli spazi a Massimiliano. Annuncio: ${form.listingUrl || "[link]"}`
    : `Ciao, vorrei un sopralluogo di progetto per un immobile a ${form.city || "[città]"}${form.sqm ? ` di circa ${form.sqm} mq` : ""}, da trasformare in ${form.propertyType || "struttura ricettiva"}.`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={() => setOpen(false)}
        >
          <motion.div
            data-testid="sopralluogo-modal"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-[#383029] bg-[#12100E] p-7 sm:p-9"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="section-kicker">{rinnovo ? "Valutazione degli spazi" : "Sopralluogo di progetto"}</p>
                <p className="mt-2 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">
                  {rinnovo ? "Raccontaci la tua struttura" : "Raccontaci il tuo spazio"}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#A89F91]">
                  Ti richiama Massimiliano entro 48 ore lavorative per fissare il sopralluogo. Le condizioni te le dice prima, per iscritto.
                </p>
              </div>
              <button data-testid="sopralluogo-close" onClick={() => setOpen(false)} aria-label="Chiudi" className="rounded-full border border-[#383029] p-2 text-[#A89F91] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#FAF7F2]">
                <X className="h-4 w-4" />
              </button>
            </div>

            {done ? (
              <div data-testid="sopralluogo-success" className="mt-8 border border-[#383029] p-6">
                <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Ricevuto.</p>
                <p className="mt-2 text-sm text-[#C4BBB0]">Massimiliano ti contatta entro 48 ore lavorative per concordare giorno e condizioni del sopralluogo.</p>
              </div>
            ) : (
              <div className="mt-7 flex flex-col gap-3">
                <input data-testid="sopralluogo-name" type="text" value={form.name} onChange={set("name")} placeholder="Come ti chiami" className={inputCls} />
                <input data-testid="sopralluogo-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="Telefono / WhatsApp" className={inputCls} />
                <input data-testid="sopralluogo-email" type="email" value={form.email} onChange={set("email")} placeholder="Email (facoltativa)" className={inputCls} />
                <input data-testid="sopralluogo-city" type="text" value={form.city} onChange={set("city")} placeholder="Città dell'immobile" className={inputCls} />
                {rinnovo ? (
                  <input data-testid="sopralluogo-listing" type="url" value={form.listingUrl} onChange={set("listingUrl")} placeholder="Link del tuo annuncio (così legge le recensioni prima)" className={inputCls} />
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <select data-testid="sopralluogo-type" value={form.propertyType} onChange={set("propertyType")} className={`${inputCls} appearance-none`}>
                      <option value="" className="bg-[#12100E]">Cosa vuoi aprire?</option>
                      {TIPI.map((t) => (
                        <option key={t} value={t} className="bg-[#12100E]">{t}</option>
                      ))}
                    </select>
                    <input data-testid="sopralluogo-sqm" type="text" inputMode="numeric" value={form.sqm} onChange={set("sqm")} placeholder="Metri quadri (circa)" className={inputCls} />
                  </div>
                )}
                <textarea data-testid="sopralluogo-message" value={form.message} onChange={set("message")} placeholder={rinnovo ? "Cosa dicono le recensioni degli spazi? (facoltativo)" : "Due righe sull'immobile: stato, piano, esterni… (facoltativo)"} rows={3} className={`${inputCls} resize-none`} />
                <label className="flex items-start gap-3 text-xs leading-relaxed text-[#A89F91]">
                  <input data-testid="sopralluogo-consent" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 shrink-0 accent-[#E05A2C]" />
                  <span>
                    Ho letto l'
                    <button type="button" onClick={openPrivacy} className="underline underline-offset-2 hover:text-[#FAF7F2]">informativa privacy</button>
                    {" "}e acconsento a essere ricontattato per questa richiesta.
                  </span>
                </label>
                {error && (
                  <p data-testid="sopralluogo-error" role="alert" aria-live="polite" className="border-l-2 border-[#E05A2C] pl-3 text-sm text-[#E05A2C]">{error}</p>
                )}
                <button
                  data-testid="sopralluogo-submit"
                  onClick={submit}
                  disabled={sending}
                  className="btn-glow mt-2 min-h-[44px] rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98] disabled:opacity-50"
                >
                  {sending ? "Invio…" : rinnovo ? "Invia — ti richiama Massimiliano" : "Richiedi il sopralluogo"}
                </button>
                <a
                  href={whatsappUrl(waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sopralluogo-whatsapp"
                  className="min-h-[44px] self-start py-2 text-sm text-[#A89F91] underline decoration-[#383029] underline-offset-4 transition-colors duration-300 hover:text-[#FAF7F2]"
                >
                  Preferisci WhatsApp? Scrivici da qui
                </a>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
